import React from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import { tokens } from "../../theme";
import { toneColors } from "../statusTone";

/**
 * One employee's whole month compressed to a single row of coded cells — the
 * muster view, scoped to a person. Doubles as a control: clicking a day selects
 * it, which the table below uses to scroll to and highlight that row.
 */

export { toneColors };

// Only the codes actually present in the month, so the legend stays short and
// never explains a code the reader can't see.
export const MonthStripLegend = ({ days = [] }) => {
  const seen = new Map();
  days.forEach((d) => {
    if (d.status.key === "UPCOMING" || seen.has(d.code)) return;
    seen.set(d.code, {
      label: d.status.key === "ON_LEAVE" ? d.leave?.name || d.status.label : d.status.label,
      tone: d.status.tone,
    });
  });

  return (
    <Stack
      direction="row"
      sx={{ gap: 2, flexWrap: "wrap", mt: 2, pt: 1.5, borderTop: `1px solid ${tokens.border}` }}
    >
      {[...seen.entries()].map(([code, { label, tone }]) => (
        <Stack key={code} direction="row" alignItems="center" spacing={0.625}>
          <Box
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: toneColors(tone).fg,
              background: toneColors(tone).bg,
              borderRadius: "6px",
              px: 0.5,
              lineHeight: 1.5,
            }}
          >
            {code}
          </Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{label}</Box>
        </Stack>
      ))}
      {/* only when the month actually contains one — an unexplained asterisk is
          worse than none, and explaining one nobody can see is clutter */}
      {days.some((d) => d.corrected) && (
        <Stack direction="row" alignItems="center" spacing={0.625}>
          <Box
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: tokens.text,
              lineHeight: 1.5,
              px: 0.5,
            }}
          >
            *
          </Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
            Punch corrected
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

const MonthStrip = ({ days = [], selectedDay, onSelectDay, todayString }) => (
  <Box sx={{ overflowX: "auto", pb: 0.5 }}>
    {/* cells share the full width; below ~31 x 2.25rem the row scrolls instead of crushing */}
    <Stack direction="row" spacing={0.25} sx={{ minWidth: "min-content", alignItems: "stretch" }}>
      {days.map((d) => {
        const { fg, bg } = toneColors(d.status.tone);
        const isSelected = selectedDay === d.dayString;
        const isUpcoming = d.status.key === "UPCOMING";
        const isToday = d.dayString === todayString;
        // a new week starts on Sunday; the gap gives the eye a place to count from
        const startsWeek = d.weekday === "Sun" && d.dayNum !== 1;

        return (
          <Tooltip
            key={d.dayString}
            title={`${d.weekday} ${d.dayNum} — ${d.remark || d.status.label}${
              d.corrected ? " · corrected" : ""
            }`}
            arrow
          >
            <Box
              role="button"
              tabIndex={isUpcoming ? -1 : 0}
              aria-pressed={isSelected}
              aria-label={`${d.weekday} ${d.dayNum}, ${d.remark || d.status.label}`}
              onClick={() => !isUpcoming && onSelectDay?.(isSelected ? null : d.dayString)}
              onKeyDown={(e) => {
                if (isUpcoming || (e.key !== "Enter" && e.key !== " ")) return;
                e.preventDefault();
                onSelectDay?.(isSelected ? null : d.dayString);
              }}
              sx={{
                flex: "1 1 0",
                minWidth: "2.25rem",
                textAlign: "center",
                cursor: isUpcoming ? "default" : "pointer",
                userSelect: "none",
                ml: startsWeek ? "0.625rem" : 0,
                borderRadius: "8px",
                "&:focus-visible": {
                  outline: `2px solid ${tokens.accent}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <Box
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: d.isWeeklyOff ? tokens.textMuted : tokens.text,
                  opacity: d.isWeeklyOff ? 0.55 : 0.75,
                }}
              >
                {d.weekday.charAt(0)}
              </Box>
              <Box
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: isToday ? 700 : 500,
                  fontVariantNumeric: "tabular-nums",
                  color: isToday ? tokens.text : tokens.textMuted,
                  mb: 0.5,
                }}
              >
                {d.dayNum}
              </Box>
              <Box
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  lineHeight: 1.5,
                  height: "2.25rem",
                  borderRadius: "8px",
                  color: isUpcoming ? tokens.textMuted : fg,
                  background: isUpcoming ? "transparent" : bg,
                  border: isUpcoming
                    ? `1px dashed ${tokens.border}`
                    : `1px solid ${isSelected ? fg : "transparent"}`,
                  boxShadow: isSelected ? `0 0 0 2px ${bg}, 0 0 0 3px ${fg}` : "none",
                  opacity: isUpcoming ? 0.6 : 1,
                  position: "relative", // anchors the corrected-day asterisk
                  transition: "box-shadow 120ms cubic-bezier(0.2, 0, 0.2, 1), border-color 120ms",
                }}
              >
                {d.code}
                {/* superscript, not a wider code: the cell is fixed width and
                    "P*" inline would shove the letter off-centre against every
                    uncorrected day in the row */}
                {d.corrected && (
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      position: "absolute",
                      top: "0.125rem",
                      right: "0.3125rem",
                      fontSize: "0.8125rem",
                      lineHeight: 1,
                    }}
                  >
                    *
                  </Box>
                )}
              </Box>
              <Box
                aria-hidden
                sx={{
                  height: "2px",
                  mt: 0.5,
                  mx: "auto",
                  width: isToday ? "1.25rem" : "100%",
                  borderRadius: "6px",
                  background: isToday ? tokens.text : "transparent",
                }}
              />
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  </Box>
);

export default MonthStrip;
